import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../../config/axiosConfig';
import RecentProtocolsManager from './RecentProtocolsManager';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import { Paper, Typography, Grid, Box, Chip } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { fileService } from '../../services/FileService';
import CommonLayout from '../../components/CommonLayout';
import { FileText, Image as ImageIcon, Video as VideoIcon, Trash2 } from 'lucide-react';
import { FaEdit } from 'react-icons/fa';
import { UserContext } from '../../context/UserContext';
import { permissions } from '../../utils/permissions-utils';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import PageTitle from '../../components/PageTitle';
import { deleteButtonInTitleMenu, editButtonInTitleMenu } from '../../components/Buttons';
import './ProtocolIdViewer.scss';
import ProtocolsAiChat from './ProtocolsAiChat';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
});

// Check if device is mobile
const isMobileDevice = () => {
  return (
    typeof window !== 'undefined' &&
    (window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
  );
};

// Helper component for file icons
const FileIcon = React.memo(({ file }) => {
  const extension = file?.name?.split('.').pop()?.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|bmp)$/i.test(file.name);
  const isVideo = /\.(mp4|mov|avi|wmv)$/i.test(file.name);
  const isPdf = extension === 'pdf';
  const isDoc = /\.(doc|docx)$/i.test(file.name);

  if (isImage) return <ImageIcon size={48} className="file-item-doc-icon" />;
  if (isVideo) return <VideoIcon size={48} className="file-item-doc-icon" />;
  if (isDoc || isPdf) return <FileText size={48} className="file-item-doc-icon" />;
  return <FileText size={48} className="file-item-doc-icon" />;
});

// Helper function to get display file name without extension
const getDisplayFileName = (file) => {
  if (!file) return '';
  return file.displayName || file.name;
};

const ProtocolIdViewerSkeleton = () => {
  return (
    <div className="protocol-id-viewer-container">
      <PageTitle title={<Skeleton.Text width="200px" />} />
      <div className="details-section">
        <div className="detail-item same-line">
          <Skeleton.Text width="200px" />
        </div>
        <div className="detail-item same-line">
          <Skeleton.Text width="250px" />
        </div>
        <div className="detail-item same-line">
          <Skeleton.Text width="300px" />
        </div>
        <div className="detail-item">
          <Skeleton.Block height="100px" />
        </div>
      </div>
      <div className="file-display-section">
        <Skeleton.Text width="150px" />
        <div className="file-display-area">
          <Skeleton.Block height="100px" />
        </div>
      </div>
    </div>
  );
};

const ProtocolIdViewer = () => {
  const [protocol, setProtocol] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { id } = useParams();
  const { protocolAccessed } = RecentProtocolsManager();
  const { user } = useContext(UserContext);

  const canEdit = protocol && permissions.canEdit(user, { department: protocol.departments?.[0] });

  useEffect(() => {
    if (id) {
      protocolAccessed(id);
    }
  }, [id, protocolAccessed]);

  useEffect(() => {
    const fetchProtocol = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await API.get(`/api/protocols/view/${id}`);
        const protocolData = response.data;

        if (protocolData.files && protocolData.files.length > 0) {
          // Pre-fetch signed URLs for all files to cache them
          const filesWithSas = await Promise.all(
            protocolData.files.map(async (file) => {
              try {
                const sasUrl = await fileService.getSignedUrl(file.url);
                return { ...file, sasUrl };
              } catch (error) {
                console.error(`Failed to get signed URL for ${file.name}:`, error);
                return { ...file, sasUrl: null }; // Handle error for individual files
              }
            })
          );
          protocolData.files = filesWithSas;
        }

        setProtocol(protocolData);
      } catch (err) {
        console.error('Error fetching protocol details for ID viewer:', err);
        setError(err.response?.data?.message || 'Failed to load protocol details');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchProtocol();
    }
  }, [id]);

  const openDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSuccess = () => {
    navigate('/protocols');
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/api/protocols/${id}`);
      setDeleteConfirmOpen(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error('Deletion failed:', error);
      const message = error.response?.data?.message || 'שגיאה במחיקת הפרוטוקול. אנא נסה שנית.';
      setErrorMessage(message);
      setShowError(true);
    }
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'לא זמין';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }, []);

  if (isLoading) {
    return (
      <CommonLayout>
        <ProtocolIdViewerSkeleton />
      </CommonLayout>
    );
  }

  if (error) {
    return (
      <CommonLayout>
        <div className="error-container">
          <Typography color="error" align="center">{error}</Typography>
        </div>
      </CommonLayout>
    );
  }

  if (!protocol) {
    return (
      <CommonLayout>
        <div className="error-container">
          <Typography align="center">הפרוטוקול לא נמצא</Typography>
        </div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div className="protocol-id-viewer-container">
            <PageTitle title={protocol.name}>
              {canEdit && (
                <>
                  <button onClick={openDeleteConfirm} style={deleteButtonInTitleMenu} title="מחק פרוטוקול">
                    <Trash2 size={16} />
                  </button>
                  <button onClick={() => navigate(`/protocols/${id}/edit`)} style={editButtonInTitleMenu} title="ערוך פרוטוקול">
                    <FaEdit size={16} />
                  </button>
                </>
              )}
            </PageTitle>

            <div className="details-section">
              {protocol.tags && protocol.tags.length > 0 && (
                <div className="detail-item same-line">
                  <span className="detail-label">תגיות:</span>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {protocol.tags.map((tag, index) => (
                      <Chip
                        key={`tag-${tag}-${index}`}
                        label={tag}
                        className="chip"
                        size="small"
                      />
                    ))}
                  </Box>
                </div>
              )}
              {((protocol.authors && protocol.authors.length > 0) || protocol.author) && (
                <div className="detail-item same-line">
                  <span className="detail-label">מחבר/ת:</span>
                  <Typography>
                    {protocol.authors && protocol.authors.length > 0
                      ? protocol.authors.join(', ')
                      : protocol.author}
                  </Typography>
                </div>
              )}
              {protocol.lastValidatedAt && (
                <div className="detail-item same-line">
                  <span className="detail-label">תאריך תיקוף אחרון:</span>
                  <Typography>{formatDate(protocol.lastValidatedAt)}</Typography>
                  {(() => {
                    const threeYearsAgo = new Date();
                    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
                    const lastValidated = new Date(protocol.lastValidatedAt);
                    if (lastValidated < threeYearsAgo && canEdit) {
                      return (
                        <Chip
                          label="נדרש תיקוף"
                          color="error"
                          size="small"
                          sx={{ marginRight: 1 }}
                        />
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              {protocol.summary && (
                <div className="detail-item">
                  <span className="detail-label">תקציר:</span>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{protocol.summary}</Typography>
                </div>
              )}
            </div>

            {protocol.files && protocol.files.length > 0 && (
              <div className="file-display-section">
                <Typography variant="h6" component="div" className="file-section-title">
                  קבצים מצורפים:
                </Typography>
                <div className="file-display-area">
                  {protocol.files.map((file, index) => {
                    const isMobile = isMobileDevice();
                    const linkProps = {
                      className: 'file-item-container',
                      href: isMobile ? (file.sasUrl || '#') : `/protocols/${id}/view?fileIndex=${index}`,
                      target: isMobile ? '_blank' : '_self',
                      rel: 'noopener noreferrer',
                      download: isMobile ? file.name : undefined,
                    };

                    return (
                      <a key={index} {...linkProps} style={{ textDecoration: 'none' }} >
                        <FileIcon file={file} />
                        <span style={{ color: '#103C6E' }} className="file-item-doc-name" title={getDisplayFileName(file.name)}>
                          {getDisplayFileName(file)}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            <DeleteConfirmationDialog
              open={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
              onConfirm={handleDelete}
              title={`האם אתה בטוח שברצונך למחוק את הפרוטוקול ${protocol?.name}?`}
              showError={showError}
              errorMessage={errorMessage}
              onCloseError={() => setShowError(false)}
            />
          </div>
          <ProtocolsAiChat hasProtocols={!!protocol} />
        </ThemeProvider>
      </CacheProvider>
    </CommonLayout>
  );
};

export default ProtocolIdViewer;
